import React, { useState, useEffect, useCallback } from "react";
import {
    Building2,
    Users,
    FolderKanban,
    Key,
    Webhook,
    Search,
    Plus,
    Crown,
    Loader2,
    AlertCircle,
    Settings,
    Shield,
    Globe
} from "lucide-react";
import {
    fetchCurrentOrganization,
    createOrganization,
    fetchWorkspaces,
    fetchOrgMembers,
    isOnSubdomainPortal,
    getStoredOrgContext
} from "../../api/organizations";
import { getOrganizationLimits } from "../../utils/organizationHelpers";
import OrgBreadcrumb from "./OrgBreadcrumb";
import OrganizationOverview from "./OrganizationOverview";
import WorkspacesList from "./WorkspacesList";
import WorkspaceDetail from "./WorkspaceDetail";
import GroupDetail from "./GroupDetail";
import MembersList from "./MembersList";
import OrgApiKeysTab from "./OrgApiKeysTab";
import OrgWebhooksTab from "./OrgWebhooksTab";
import OrgSSOSettings from "./OrgSSOSettings";
import OrgPortalSettings from "./OrgPortalSettings";
import CreateEntityModal from "./CreateEntityModal";

const TABS = [
    { id: "overview", label: "Overview", icon: Building2 },
    { id: "workspaces", label: "Workspaces", icon: FolderKanban },
    { id: "members", label: "Members", icon: Users },
    { id: "portal", label: "Portal", icon: Globe },
    { id: "sso", label: "SSO", icon: Shield },
    { id: "api-keys", label: "API Keys", icon: Key },
    { id: "webhooks", label: "Webhooks", icon: Webhook }
];

const OrganizationManager = ({ featureFlags = {}, onUpgradePlan, subscription, userEmail }) => {
    const [organization, setOrganization] = useState(null);
    const [workspaces, setWorkspaces] = useState([]);
    const [members, setMembers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");
    const [activeTab, setActiveTab] = useState("overview");
    const [searchQuery, setSearchQuery] = useState("");

    // Navigation state for drill-down
    const [selectedWorkspace, setSelectedWorkspace] = useState(null);
    const [selectedGroup, setSelectedGroup] = useState(null);

    // Create modal state
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [createModalType, setCreateModalType] = useState(null); // 'organization', 'workspace', 'group'

    const tier = subscription?.plan?.name || subscription?.tier || "ultra";
    const limits = getOrganizationLimits(tier);

    // Get current user's role in the organization (admin/owner can edit, member is read-only for sensitive ops)
    // Prefer fresh member data over stored login context (which may be stale)
    const storedOrgContext = getStoredOrgContext();
    // Case-insensitive email comparison
    const currentUserMember = members.find(m =>
        m.email?.toLowerCase() === userEmail?.toLowerCase()
    );
    // Check multiple possible role field names from API
    const memberRole = currentUserMember?.role ||
                       currentUserMember?.role_id ||
                       currentUserMember?.org_role ||
                       currentUserMember?.organization_role;
    // Prioritize member data (fresh) over stored context (may be stale from login)
    const userRole = memberRole || storedOrgContext?.org_role || 'member';
    const canEditOrg = ['admin', 'owner'].includes(userRole?.toLowerCase());

    // Check if user has organizations feature
    const hasAccess = featureFlags.hasOrganizations;

    const loadOrganization = useCallback(async () => {
        if (!hasAccess) return;

        setIsLoading(true);
        setError("");
        try {
            // Use fetchCurrentOrganization which handles subdomain portal context
            // On subdomain: uses org_id from login response
            // On main site: uses GET /api/organizations/me
            const response = await fetchCurrentOrganization();
            // Handle various response structures from backend
            const orgData = response?.data || response || null;

            if (orgData && (orgData.org_id || orgData.id)) {
                // Normalize org_id to id for consistent frontend usage
                const normalizedOrg = {
                    ...orgData,
                    id: orgData.org_id || orgData.id
                };
                setOrganization(normalizedOrg);

                // Load workspaces and members
                const orgId = normalizedOrg.id;
                const [wsResponse, membersResponse] = await Promise.all([
                    fetchWorkspaces(orgId).catch(() => ({ workspaces: [] })),
                    fetchOrgMembers(orgId).catch(() => ({ members: [] }))
                ]);

                // Handle various response structures
                const wsData = wsResponse?.workspaces || wsResponse?.data?.workspaces || [];
                const membersData = membersResponse?.members || membersResponse?.data?.members || [];
                setWorkspaces(Array.isArray(wsData) ? wsData : []);
                setMembers(Array.isArray(membersData) ? membersData : []);
            }
        } catch (err) {
            console.error("Failed to load organization:", err);
            // Don't show error if simply no org exists (404)
            if (err?.status !== 404) {
                setError("Unable to load organization data.");
            }
        } finally {
            setIsLoading(false);
        }
    }, [hasAccess]);

    useEffect(() => {
        loadOrganization();
    }, [loadOrganization]);

    const handleCreateOrganization = async (data) => {
        try {
            // Transform form data to match API spec: { name, settings }
            const payload = {
                name: data.name,
                settings: data.description ? { description: data.description } : {}
            };
            const response = await createOrganization(payload);
            // Handle various response structures
            const newOrg = response?.data || response;
            if (newOrg && (newOrg.id || newOrg.org_id)) {
                setCreateModalOpen(false);
                // Reload to get fresh data with workspaces/members
                await loadOrganization();
            }
        } catch (err) {
            throw err;
        }
    };

    const handleWorkspaceSelect = (workspace) => {
        setSelectedWorkspace(workspace);
        setSelectedGroup(null);
    };

    const handleGroupSelect = (group) => {
        setSelectedGroup(group);
    };

    const handleBreadcrumbNavigate = (level) => {
        if (level === "organization") {
            setSelectedWorkspace(null);
            setSelectedGroup(null);
        } else if (level === "workspace") {
            setSelectedGroup(null);
        }
    };

    const openCreateModal = (type) => {
        setCreateModalType(type);
        setCreateModalOpen(true);
    };

    const refreshWorkspaces = async () => {
        if (!organization?.id) return;
        try {
            const response = await fetchWorkspaces(organization.id);
            // Handle various response structures
            const wsData = response?.workspaces || response?.data?.workspaces || response?.data || [];
            const normalizedWorkspaces = (Array.isArray(wsData) ? wsData : []).map(ws => ({
                ...ws,
                id: ws.workspace_id || ws.id
            }));
            setWorkspaces(normalizedWorkspaces);
        } catch (err) {
            console.error("Failed to refresh workspaces:", err);
        }
    };

    const refreshMembers = async () => {
        if (!organization?.id) return;
        try {
            const response = await fetchOrgMembers(organization.id);
            // Handle various response structures
            const membersData = response?.members || response?.data?.members || response?.data || [];
            setMembers(Array.isArray(membersData) ? membersData : []);
        } catch (err) {
            console.error("Failed to refresh members:", err);
        }
    };

    // Upgrade prompt for non-Ultra users
    if (!hasAccess) {
        return (
            <div className="flex flex-col h-full bg-[#f7f4ee] dark:bg-slate-950">
                <div className="flex-1 flex items-center justify-center p-6">
                    <div className="max-w-md text-center">
                        <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
                            <Crown className="w-8 h-8 text-white" />
                        </div>
                        <h2 className="text-2xl font-bold dark:text-white text-slate-900 mb-3">
                            Organizations & Workspaces
                        </h2>
                        <p className="dark:text-slate-400 text-slate-600 mb-6">
                            Create organizations, workspaces, and groups to manage your enterprise mortgage operations.
                            Assign roles, control access, and scale your team collaboration.
                        </p>
                        <button
                            onClick={onUpgradePlan}
                            className="px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white rounded-xl font-semibold transition-all shadow-lg shadow-amber-500/25"
                        >
                            Upgrade to Ultra
                        </button>
                        <p className="text-xs dark:text-slate-500 text-slate-400 mt-4">
                            Available on Ultra and Enterprise plans
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // No organization yet - show create prompt
    if (!isLoading && !organization) {
        return (
            <div className="flex flex-col h-full bg-[#f7f4ee] dark:bg-slate-950">
                <div className="flex-1 flex items-center justify-center p-6">
                    <div className="max-w-md text-center">
                        <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center">
                            <Building2 className="w-8 h-8 text-white" />
                        </div>
                        <h2 className="text-2xl font-bold dark:text-white text-slate-900 mb-3">
                            Create Your Organization
                        </h2>
                        <p className="dark:text-slate-400 text-slate-600 mb-6">
                            Set up your organization to start managing workspaces, groups, and team members.
                        </p>
                        <button
                            onClick={() => openCreateModal("organization")}
                            className="px-6 py-3 bg-teal-600 hover:bg-teal-500 text-white rounded-xl font-semibold transition-colors flex items-center gap-2 mx-auto"
                        >
                            <Plus className="w-5 h-5" />
                            Create Organization
                        </button>
                    </div>
                </div>

                {createModalOpen && createModalType === "organization" && (
                    <CreateEntityModal
                        type="organization"
                        onClose={() => setCreateModalOpen(false)}
                        onSubmit={handleCreateOrganization}
                    />
                )}
            </div>
        );
    }

    // Loading state
    if (isLoading) {
        return (
            <div className="flex flex-col h-full bg-[#f7f4ee] dark:bg-slate-950">
                <div className="flex-1 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
                </div>
            </div>
        );
    }

    // Drill-down views
    if (selectedGroup && selectedWorkspace) {
        return (
            <div className="flex flex-col h-full bg-[#f7f4ee] dark:bg-slate-950">
                <OrgBreadcrumb
                    organization={organization}
                    workspace={selectedWorkspace}
                    group={selectedGroup}
                    onNavigate={handleBreadcrumbNavigate}
                />
                <GroupDetail
                    organization={organization}
                    workspace={selectedWorkspace}
                    group={selectedGroup}
                    onBack={() => setSelectedGroup(null)}
                    userEmail={userEmail}
                    userRole={userRole}
                    canEdit={canEditOrg}
                />
            </div>
        );
    }

    if (selectedWorkspace) {
        return (
            <div className="flex flex-col h-full bg-[#f7f4ee] dark:bg-slate-950">
                <OrgBreadcrumb
                    organization={organization}
                    workspace={selectedWorkspace}
                    onNavigate={handleBreadcrumbNavigate}
                />
                <WorkspaceDetail
                    organization={organization}
                    workspace={selectedWorkspace}
                    onGroupSelect={handleGroupSelect}
                    onBack={() => setSelectedWorkspace(null)}
                    limits={limits}
                    userEmail={userEmail}
                    userRole={userRole}
                    canEdit={canEditOrg}
                />
            </div>
        );
    }

    // Main organization view with tabs
    return (
        <div className="flex flex-col h-full bg-[#f7f4ee] dark:bg-slate-950">
            {/* Hero Section */}
            <div className="relative overflow-hidden px-6 py-10 sm:py-12">
                <div
                    className="absolute inset-0"
                    style={{
                        background: 'linear-gradient(120deg, rgba(13, 148, 136, 0.98), rgba(15, 23, 42, 0.95))'
                    }}
                />
                <div
                    className="absolute inset-0 opacity-70"
                    style={{
                        backgroundImage: 'radial-gradient(circle at 15% 15%, rgba(255,255,255,0.3), transparent 40%), radial-gradient(circle at 80% 10%, rgba(255,255,255,0.2), transparent 35%)'
                    }}
                />
                <div className="relative max-w-5xl mx-auto">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                        <div className="text-white">
                            <p className="text-sm uppercase tracking-[0.2em] text-white/70">Organization</p>
                            <h2 className="text-3xl sm:text-4xl font-semibold mt-2">
                                {organization?.name || "My Organization"}
                            </h2>
                            <p className="text-sm sm:text-base text-white/80 mt-2 max-w-xl">
                                Manage workspaces, groups, and team members across your enterprise.
                            </p>
                        </div>
                        <div className="w-full lg:w-[360px]">
                            <label className="text-xs font-semibold text-white/80 uppercase tracking-[0.2em]">
                                Search
                            </label>
                            <div className="relative mt-3">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Search workspaces, members..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-12 pr-4 py-3 bg-white/95 dark:bg-slate-800 border-0 rounded-xl shadow-lg text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-4 focus:ring-teal-200"
                                />
                            </div>
                            <div className="flex flex-wrap gap-2 mt-4 text-xs text-white/80">
                                <span className="px-3 py-1.5 rounded-full bg-white/15 text-xs font-medium">
                                    {workspaces.length} / {limits.workspaces} workspaces
                                </span>
                                <span className="px-3 py-1.5 rounded-full bg-white/15 text-xs font-medium">
                                    {members.length} / {limits.members} members
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="px-6 -mt-4 relative z-10">
                <div className="max-w-5xl mx-auto">
                    <div className="flex gap-1 p-1 dark:bg-slate-900/80 bg-white/80 backdrop-blur-sm rounded-xl shadow-sm border dark:border-slate-800 border-slate-200 w-fit">
                        {TABS.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                    activeTab === tab.id
                                        ? "bg-teal-600 text-white shadow-md"
                                        : "dark:text-slate-400 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800"
                                }`}
                            >
                                <tab.icon className="w-4 h-4" />
                                <span className="hidden sm:inline">{tab.label}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="px-6 mt-4">
                    <div className="max-w-5xl mx-auto">
                        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-sm text-red-400">
                            <AlertCircle className="w-4 h-4" />
                            {error}
                        </div>
                    </div>
                </div>
            )}

            {/* Tab Content */}
            <div className="flex-1 overflow-auto px-6 py-6">
                <div className="max-w-5xl mx-auto">
                    {activeTab === "overview" && (
                        <OrganizationOverview
                            organization={organization}
                            workspaces={workspaces}
                            members={members}
                            limits={limits}
                        />
                    )}
                    {activeTab === "workspaces" && (
                        <WorkspacesList
                            organization={organization}
                            workspaces={workspaces}
                            onSelect={handleWorkspaceSelect}
                            onCreateNew={() => openCreateModal("workspace")}
                            searchQuery={searchQuery}
                            limits={limits}
                            onRefresh={refreshWorkspaces}
                            userRole={userRole}
                            canEdit={canEditOrg}
                        />
                    )}
                    {activeTab === "members" && (
                        <MembersList
                            organization={organization}
                            members={members}
                            searchQuery={searchQuery}
                            limits={limits}
                            userEmail={userEmail}
                            onRefresh={refreshMembers}
                            showInviteForm={canEditOrg}
                            userRole={userRole}
                            canEdit={canEditOrg}
                        />
                    )}
                    {activeTab === "portal" && (
                        <OrgPortalSettings
                            organization={organization}
                            subscription={subscription}
                            userRole={userRole}
                        />
                    )}
                    {activeTab === "sso" && (
                        <OrgSSOSettings
                            organization={organization}
                            userRole={userRole}
                            canEdit={canEditOrg}
                        />
                    )}
                    {activeTab === "api-keys" && (
                        <OrgApiKeysTab
                            organization={organization}
                            limits={limits}
                            userRole={userRole}
                            canEdit={canEditOrg}
                        />
                    )}
                    {activeTab === "webhooks" && (
                        <OrgWebhooksTab
                            organization={organization}
                            limits={limits}
                            userRole={userRole}
                            canEdit={canEditOrg}
                        />
                    )}
                </div>
            </div>

            {/* Create Modal */}
            {createModalOpen && (
                <CreateEntityModal
                    type={createModalType}
                    onClose={() => setCreateModalOpen(false)}
                    onSubmit={async (data) => {
                        if (createModalType === "organization") {
                            await handleCreateOrganization(data);
                        } else if (createModalType === "workspace") {
                            // Handle in WorkspacesList
                        }
                        setCreateModalOpen(false);
                        refreshWorkspaces();
                    }}
                    organization={organization}
                />
            )}
        </div>
    );
};

export default OrganizationManager;
